import { useRef, useLayoutEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Mobile browsers fire a 'resize' event when the address bar hides/shows on
// scroll. Without this, that resize triggers a ScrollTrigger refresh mid-page
// that can recompute a trigger's start/end as "not yet entered" and reverse
// an already-played reveal back to opacity 0 — with nothing left to scroll
// past, it never plays again. This is GSAP's documented fix for that.
ScrollTrigger.config({ ignoreMobileResize: true });

/**
 * Wraps children in a div that fades/rises into view as it scrolls
 * onto screen. Pass `stagger` + give each direct child a `data-reveal-item`
 * (or just multiple children) to stagger them individually.
 */
export function Reveal({
  children,
  as: Tag = 'div',
  y = 40,
  rotateX = 0,
  transformOrigin,
  perspective,
  ease = 'power3.out',
  duration = 0.9,
  delay = 0,
  stagger = 0,
  start = 'top 85%',
  end,
  scrub = false,
  once = false,
  style,
  className,
  ...rest
}) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const targets = stagger ? el.children : el;

    // y === 0 means "fade only" — some elements in this codebase are
    // centered via `transform: translateX(-50%)`, and GSAP writing any
    // transform (even translate3d(0,0,0)) would permanently turn them
    // into a new containing block for absolutely-positioned children.
    const fromVars = { opacity: 0 };
    const toVars = {
      opacity: 1,
      duration,
      stagger,
      ease,
      scrollTrigger: scrub
        ? {
            // scrub ties animation progress directly to scroll position
            // (no fixed duration) instead of playing once when triggered —
            // it plays forward AND backward in lockstep with the scrollbar.
            trigger: el,
            start,
            end: end || 'bottom 60%',
            scrub: scrub === true ? 0.3 : scrub,
          }
        : {
            trigger: el,
            start,
            toggleActions: once ? 'play none none none' : 'play none none reverse',
          },
    };
    if (!scrub) {
      toVars.delay = delay;
    }
    if (y) {
      fromVars.y = y;
      toVars.y = 0;
    }
    if (rotateX) {
      fromVars.rotateX = rotateX;
      toVars.rotateX = 0;
    }
    if (transformOrigin) {
      fromVars.transformOrigin = transformOrigin;
      toVars.transformOrigin = transformOrigin;
    }
    if (perspective) {
      fromVars.transformPerspective = perspective;
      toVars.transformPerspective = perspective;
    }

    const ctx = gsap.context(() => {
      // Many elements in this codebase carry an inline `transition: 'all ...'`
      // for their own hover effect — left alone, the browser would try to
      // CSS-transition every per-frame value GSAP writes, fighting GSAP's
      // easing and causing stutter. React re-applies the hover transition
      // itself on the next hover, so it's safe to clear it here.
      gsap.set(targets, { transition: 'none' });
      gsap.fromTo(targets, fromVars, toVars);
    }, el);

    return () => ctx.revert();
  }, [y, rotateX, transformOrigin, perspective, ease, duration, delay, stagger, start, end, scrub, once]);

  return (
    <Tag ref={ref} style={style} className={className} {...rest}>
      {children}
    </Tag>
  );
}

export default Reveal;
